import { RealtimeStatus, useRealtimeStatus } from "@tcg/realtime/react";
import { DropdownMenu } from "radix-ui";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth/use-auth";

import { saveLiveAlertPrefs, useLiveAlertPrefs } from "./alertPrefs";

/**
 * "● Live" in the top bar (T11-52 step 4, design/04 §0 R21 row): colour + word of the one realtime
 * connection ("● Live" / "○ Reconnecting…" / "○ Updates every 30 s" / "○ Offline"). Opening it
 * switches the live alerts (message / sound) for this person on this device.
 */
export function LiveStatusMenu() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const staffId = session?.user.id ?? "anonymous";
  const prefs = useLiveAlertPrefs(staffId);
  const status = useRealtimeStatus();
  const item =
    "flex min-h-touch cursor-pointer items-center gap-3 rounded px-3 text-sm outline-none data-[highlighted]:bg-accent";
  if (status === "idle") {
    return null; // nothing on this screen listens (or realtime is switched off)
  }
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={t("live.menu")}
        data-slot="live-status"
        className="inline-flex min-h-touch items-center rounded-md px-2 text-xs font-semibold hover:bg-accent"
      >
        <RealtimeStatus className="max-md:[&>span:last-child]:sr-only" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-50 w-72 rounded-md border bg-popover p-1 text-popover-foreground shadow-lg"
        >
          <DropdownMenu.Label className="px-3 py-2 text-sm">
            <span className="block font-semibold">{t("live.menu")}</span>
            <span className="block text-xs text-muted-foreground">{t("live.menu_hint")}</span>
          </DropdownMenu.Label>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.CheckboxItem
            className={item}
            checked={prefs.toasts}
            onSelect={(event) => {
              event.preventDefault();
            }}
            onCheckedChange={(checked) => {
              saveLiveAlertPrefs(staffId, { ...prefs, toasts: checked });
            }}
          >
            <span
              aria-hidden
              className="inline-flex size-5 items-center justify-center rounded border"
            >
              {prefs.toasts ? "✓" : ""}
            </span>
            {t("live.toasts")}
          </DropdownMenu.CheckboxItem>
          <DropdownMenu.CheckboxItem
            className={item}
            checked={prefs.sound}
            disabled={!prefs.toasts}
            onSelect={(event) => {
              event.preventDefault();
            }}
            onCheckedChange={(checked) => {
              saveLiveAlertPrefs(staffId, { ...prefs, sound: checked });
            }}
          >
            <span
              aria-hidden
              className="inline-flex size-5 items-center justify-center rounded border"
            >
              {prefs.sound ? "✓" : ""}
            </span>
            {t("live.sound")}
          </DropdownMenu.CheckboxItem>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
